<template>
  <div>
    <NavBar />
    <div class="container">
      <div class="row d-print-none">
        <div class="col">
          <h1>
            Reports <small>{{ organizationDisplayName }}</small>
          </h1>
        </div>
      </div>
      <div class="row d-print-none">
        <div class="col">
          <div class="py-4">
            <form class="form-inline w-100" @submit.prevent>
              <div class="btn-group mr-2" role="group" data-bs-toggle="tooltip" data-bs-delay="500" title="Filter by interview status">
                <button type="button" class="btn dropdown-toggle" :class="status ? 'btn-primary' : 'btn-outline-primary'" data-toggle="dropdown">
                  <fa icon="filter" />
                  {{ status ? status.displayName : "All" }}
                </button>
                <div class="dropdown-menu">
                  <button type="button" class="dropdown-item" @click="status = null">All</button>
                  <button v-for="s in reportStatuses" :key="s.code" type="button" class="dropdown-item" @click="status = s">
                    {{ s.displayName }}
                  </button>
                </div>
              </div>
              <div class="input-group d-flex flex-fill">
                <input
                  type="text"
                  class="form-control"
                  placeholder="Search for a report"
                  v-model="query"
                  data-bs-toggle="tooltip"
                  data-bs-delay="500"
                  title="Search by Registration ID, Email Address, Interview Number, or Investigation Name."
                />
                <div class="input-group-append">
                  <button type="button" class="btn btn-primary" @click="search">
                    <fa icon="search" />
                    <span class="ml-2">Search</span>
                  </button>
                  <button type="button" class="btn btn-outline-primary" @click="refresh">
                    <fa icon="redo" />
                    <span class="ml-2">Refresh</span>
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>
        <div class="col-md-auto">
          <div class="d-flex py-4">
            <div class="ml-auto">
              <button
                v-if="isListView && isVerensicsAdministrator"
                type="button"
                class="btn btn-outline-primary mr-2"
                @click="detailsExport"
                :disabled="isExporting"
              >
                <fa v-if="isExporting" icon="circle-notch" spin />
                <fa v-if="!isExporting" icon="file-excel" />
                <span class="ml-2">Export Details</span>
              </button>
              <button v-if="isListView" type="button" class="btn btn-outline-primary mr-2" @click="listExport" :disabled="isExporting">
                <fa v-if="isExporting" icon="circle-notch" spin />
                <fa v-if="!isExporting" icon="file-excel" />
                <span class="ml-2">Export List</span>
              </button>
              <button v-if="isStatisticsView" type="button" class="btn btn-outline-primary mr-2" @click="statisticsExport" :disabled="isExporting">
                <fa v-if="isExporting" icon="circle-notch" spin />
                <fa v-if="!isExporting" icon="file-pdf" />
                <span class="ml-2">Export Statistics</span>
              </button>
              <button type="button" class="btn btn-primary" @click="createTest">
                <fa icon="plus" />
                <span class="ml-2">Create</span>
              </button>
            </div>
          </div>
        </div>
      </div>
      <div class="row d-print-none">
        <div class="col">
          <Filters ref="filters" :organizationId="organizationId" @changed="onFiltersChanged" />
        </div>
      </div>
      <div class="row d-print-none">
        <div class="col text-center">
          <div class="btn-group btn-group-toggle mb-3" data-toggle="buttons">
            <label
              class="btn btn-outline-primary"
              :class="{ active: view === 'list' }"
              @click="view = 'list'"
              data-bs-toggle="tooltip"
              data-bs-delay="500"
              title="View the list of reports"
            >
              <input type="radio" id="view-list" value="list" autocomplete="off" />
              <fa icon="list" />
              Reports
            </label>
            <label
              class="btn btn-outline-primary"
              :class="{ active: view === 'statistics' }"
              @click="view = 'statistics'"
              data-bs-toggle="tooltip"
              data-bs-delay="500"
              title="View statistics about completed interviews"
            >
              <input type="radio" id="view-statistics" value="statistics" autocomplete="off" />
              <fa icon="chart-pie" />
              Statistics
            </label>
          </div>
        </div>
      </div>
      <div v-if="isListView" class="row d-print-none">
        <div class="col">
          <h5 v-if="page.total" class="pb-2">Results: {{ page.total.toLocaleString() }}</h5>
        </div>
      </div>
      <div v-if="isListView" class="row">
        <div class="col">
          <div class="table-responsive">
            <table class="table table-striped">
              <tbody>
                <tr v-if="isLoading" class="text-center text-muted">
                  <td><fa icon="circle-notch" spin /> Loading...</td>
                </tr>
                <tr v-if="error" class="text-center text-muted">
                  <td>Sorry. There was an error. Please try again.</td>
                </tr>
                <tr v-if="page.total === 0" class="text-center text-muted">
                  <td>No results</td>
                </tr>
                <tr v-for="item in page.items" :key="item.id">
                  <Row :organizationId="organizationId" :report="item" />
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
      <div v-if="isListView && needsPaging" class="row">
        <div class="col">
          <div class="row justify-content-between p-3">
            <button type="button" class="btn btn-outline-dark" @click="prev" :disabled="!hasPrev">Previous</button>
            <button type="button" class="btn btn-outline-dark" @click="next" :disabled="!hasNext">Next</button>
          </div>
        </div>
      </div>
      <div v-if="isStatisticsView" class="row">
        <div class="col px-0">
          <Statistics
            :organizationId="organizationId"
            :query="query"
            :status="status"
            :dateRange="dateRange"
            :regionIds="regionIds"
            :facilityIds="facilityIds"
            :companyIds="companyIds"
            :functionIds="functionIds"
            :countryIds="countryIds"
            :stateIds="stateIds"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import debounce from "debounce";
import NavBar from "@/views/reports/NavBar.vue";
import Filters from "@/views/reports/report/list-reports/Filters.vue";
import Row from "@/views/reports/report/list-reports/Row.vue";
import Statistics from "@/views/reports/report/statistics-reports/Statistics.vue";

export default {
  components: {
    NavBar,
    Filters,
    Row,
    Statistics,
  },
  props: {
    organizationId: String,
  },
  data() {
    return {
      isLoading: false,
      isExporting: false,

      view: "list",

      skip: 0,
      take: 25,
      query: null,
      status: null,
      dateRange: {},
      regionIds: [],
      facilityIds: [],
      companyIds: [],
      functionIds: [],
      countryIds: [],
      stateIds: [],

      page: {},
      error: null,
    };
  },
  computed: {
    ...mapGetters("organization", ["organization"]),
    ...mapGetters("user", ["user"]),
    ...mapGetters("role", ["isVerensicsAdministrator"]),
    ...mapGetters("static", ["reportStatuses"]),
    isListView() {
      return this.view === "list";
    },
    isStatisticsView() {
      return this.view === "statistics";
    },
    hasPrev() {
      if (!this.page) return false;
      return this.skip > 0;
    },
    hasNext() {
      if (!this.page) return false;
      return this.skip + this.take < this.page.total;
    },
    needsPaging() {
      return this.hasNext || this.hasPrev;
    },
    queryString() {
      let url = `view=${this.view}&skip=${this.skip}&take=${this.take}`;
      if (this.query) url += `&query=${encodeURIComponent(this.query)}`;
      if (this.status) url += `&status=${this.status.code}`;
      if (this.dateRange && this.dateRange.start) url += `&startDate=${Math.floor(this.dateRange.start.getTime() / 1000)}`;
      if (this.dateRange && this.dateRange.end) url += `&endDate=${Math.floor(this.dateRange.end.getTime() / 1000)}`;
      this.regionIds.forEach(id => (url += `&region=${id}`));
      this.facilityIds.forEach(id => (url += `&facility=${id}`));
      this.companyIds.forEach(id => (url += `&company=${id}`));
      this.functionIds.forEach(id => (url += `&function=${id}`));
      this.countryIds.forEach(id => (url += `&country=${id}`));
      this.stateIds.forEach(id => (url += `&state=${id}`));
      return url;
    },
    organizationDisplayName() {
      if (!this.isVerensicsAdministrator) return null;
      if (this.organizationId === this.organization.id) return "(All)";
      return this.page && this.page.items && this.page.items.length ? `(${this.page.items[0].organization})` : null;
    },
  },
  watch: {
    queryString() {
      this.load();
    },
    query() {
      this.skip = 0;
    },
    dateRange() {
      this.skip = 0;
    },
    regionIds() {
      this.skip = 0;
    },
    companyIds() {
      this.skip = 0;
    },
    facilityIds() {
      this.skip = 0;
    },
    functionIds() {
      this.skip = 0;
    },
    countryIds() {
      this.skip = 0;
    },
    stateIds() {
      this.skip = 0;
    },
    user() {
      this.skip = 0;
      this.load();
    },
    organization() {
      this.skip = 0;
      this.load();
    },
    organizationId() {
      this.skip = 0;
      this.load();
    },
  },
  created() {
    // query strings
    this.view = this.$route.query.view || this.view;
    this.skip = this.$route.query.skip ? parseInt(this.$route.query.skip) : this.skip;
    this.take = this.$route.query.take ? parseInt(this.$route.query.take) : this.take;
    this.query = this.$route.query.query || this.query;
    this.status = this.reportStatuses.find(s => s.code === (this.$route.query.status || "completed")) || this.status;

    this.load = debounce(this.load, 350);
    this.load();
  },
  methods: {
    onFiltersChanged() {
      this.dateRange = this.$refs.filters.dateRange;
      this.regionIds = this.$refs.filters.selectedRegionIds;
      this.facilityIds = this.$refs.filters.selectedFacilityIds;
      this.companyIds = this.$refs.filters.selectedCompanyIds;
      this.functionIds = this.$refs.filters.selectedFunctionIds;
      this.countryIds = this.$refs.filters.selectedCountryIds;
      this.stateIds = this.$refs.filters.selectedStateIds;
    },
    load() {
      this.page = {};
      this.error = null;
      this.isLoading = true;
      this.$axios
        .get(`/v1/organizations/${this.organizationId}/reports?${this.queryString}`)
        .then(res => (this.page = res.data))
        .catch(err => (this.error = err))
        .finally(() => {
          this.isLoading = false;
          setTimeout(() => (window.printReady = true), 2000);
        });
    },
    prev() {
      this.skip = Math.max(0, this.skip - this.take);
    },
    next() {
      this.skip += this.take;
    },
    createTest() {
      this.$router.push(`/organizations/${this.organizationId}/reports/create`);
    },
    detailsExport() {
      this.isExporting = true;
      this.$axios
        .post(`/v1/organizations/${this.organizationId}/reports/details/export?${this.queryString}`)
        .then(res => {
          const link = document.createElement("a");
          link.href = res.data.url;
          link.click();
          link.remove();
        })
        .finally(() => (this.isExporting = false));
    },
    listExport() {
      this.isExporting = true;
      this.$axios
        .post(`/v1/organizations/${this.organizationId}/reports/list/export?${this.queryString}`)
        .then(res => {
          const link = document.createElement("a");
          link.href = res.data.url;
          link.click();
          link.remove();
        })
        .finally(() => (this.isExporting = false));
    },
    statisticsExport() {
      this.isExporting = true;
      this.$axios
        .post(`/v1/organizations/${this.organizationId}/reports/statistics/export?${this.queryString}`)
        .then(res => {
          const link = document.createElement("a");
          link.target = "_blank";
          link.rel = "noopener noreferrer";
          link.href = res.data.url;
          link.click();
          link.remove();
        })
        .finally(() => (this.isExporting = false));
    },
    search() {
      this.load();
    },
    refresh() {
      this.load();
    },
  },
};
</script>

<style lang="scss" scoped>
@import "@/assets/styles/base.scss";

table {
  @extend .border;

  tr {
    @extend .border-bottom;
  }
}
</style>
